import React from "react";
import { reportClientError } from "@/lib/observability";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Top-level React error boundary. Catches render-time errors anywhere in the
 * app, reports them to the observability sink, and renders a friendly fallback.
 */
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: unknown): State {
    return {
      hasError: true,
      message: err instanceof Error ? err.message : String(err),
    };
  }

  componentDidCatch(err: unknown, info: { componentStack?: string | null }): void {
    void reportClientError({
      level: "fatal",
      message: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack ?? null : null,
      componentStack: info.componentStack ?? null,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, message: undefined });
  };

  handleReload = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, message: undefined });
    window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center p-6"
        style={{ background: "hsl(40 33% 96%)" }}
      >
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-serif font-bold text-foreground">
            Something went wrong
          </h1>
          <p className="text-muted-foreground text-sm">
            This page hit an unexpected error. We've logged it so we can fix it.
          </p>
          {this.state.message && (
            <pre className="text-xs text-left bg-muted/50 rounded-md p-3 overflow-auto max-h-40">
              {this.state.message}
            </pre>
          )}
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center rounded-md border border-border bg-white text-foreground px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="inline-flex items-center justify-center rounded-md border border-border bg-white text-foreground px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              Back to cookbook
            </button>
          </div>
        </div>
      </div>
    );
  }
}
